import React, { Fragment, useCallback, useContext, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/layout/login";
import { TextField, Grid, Button, useTheme, Typography } from "@mui/material";
import * as yup from "yup";
import { Formik, Form } from "formik";
import styles from "./style.module.scss";
import { postData } from "../../services/ajax/";
import { LoadingModalContext } from "../../components/modal/loading";
import { AlertModalContext } from "../../components/modal/alert";
import { setYupLocaleES } from "../../const/yup";
import { green, red } from "@mui/material/colors";

setYupLocaleES();

const schema = yup.object().shape({
    clave1: yup
        .string()
        .required("La contraseña es obligatoria")
        .min(8, "La contraseña debe tener al menos 8 caracteres"),
    clave2: yup
        .string()
        .oneOf([yup.ref("clave1")], "Las contraseñas no coinciden")
        .required("Repetí la contraseña"),
});

function Page() {
    const theme = useTheme();
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);

    const { token } = useParams();
    const navigate = useNavigate();

    const [validador, setValidador] = useState({
        coincidencia: false,
        longitud: false,
        alfanumerico: false,
        mayuscula: false,
        minuscula: false,
        caracterEspecial: false,
    });
    const [errorMessage, setErrorMessage] = useState("");

    const onLoginSubmit = async (values, { setSubmitting }) => {
        //si pasa las validaciones, enviar la nueva clave al backend
        if(validador.coincidencia === true && validador.longitud === true &&
           validador.alfanumerico === true && validador.mayuscula === true &&
           validador.minuscula === true && validador.caracterEspecial === true) {
            loadingModal.setOpen(true);
            setErrorMessage("");

            try {
                const response = await postData(`usuarios/newpass/${token}`, {
                    contrasenia: values.clave1,
                });

                if (response.error === "") {
                    alertModal.showModal(
                        "Contraseña cambiada", 
                        "Su contraseña ha sido cambiada exitosamente. Será redirigido al inicio de sesión.",
                        () => {
                            navigate("/login");
                        }
                    );
                } else {
                    // El endpoint puede devolver error en formato JSON
                    const errorMsg = response.error || "Error al cambiar la contraseña. El token puede ser inválido o haber expirado.";
                    setErrorMessage(errorMsg);
                    alertModal.showModal("Error", errorMsg);
                }
            } catch (error) {
                const errorMsg = error.message || "Error al conectar con el servidor";
                setErrorMessage(errorMsg);
                alertModal.showModal("Error", errorMsg);
            } finally {
                loadingModal.setOpen(false);
                setSubmitting(false);
            }
        }
        else {
            alertModal.showModal("Error de validación", "La nueva contraseña no cumple con los requisitos.");
            setSubmitting(false);
        }
    };

    const validarClaves = useCallback((clave1, clave2) => {
        setValidador({
            coincidencia: clave1 === clave2 && clave1.length > 0,
            longitud: clave1.length >= 8,
            alfanumerico: /[0-9]/.test(clave1) && /[a-zA-Z]/.test(clave1),
            mayuscula: /[A-Z]/.test(clave1),
            minuscula: /[a-z]/.test(clave1),
            caracterEspecial: /[!@#$%^&*(),.?":{}|<>]/.test(clave1),
        });
    }, []);

    return (
        <Layout>
            <Grid container direction="column">
                <Fragment>
                    <h2>Nueva contraseña</h2>
                    <p>Por favor ingrese su nueva contraseña</p>

                    <Formik
                        initialValues={{ clave1: "", clave2: "" }}
                        validationSchema={schema}
                        onSubmit={onLoginSubmit}
                    >
                        {({ errors, touched, isSubmitting, values, handleChange, handleBlur }) => (
                            <Form className={styles.form}>
                                <TextField
                                    variant="standard"
                                    label="Contraseña"
                                    type="password"
                                    autoComplete="new-password"
                                    name="clave1"
                                    value={values.clave1}
                                    onChange={e => {
                                        handleChange(e);
                                        validarClaves(e.target.value, values.clave2);
                                    }}
                                    onBlur={e => {
                                        handleBlur(e);
                                        validarClaves(values.clave1, values.clave2);
                                    }}
                                    error={Boolean(errors.clave1 && touched.clave1)}
                                    helperText={
                                        errors.clave1 && touched.clave1 ? errors.clave1 : ""
                                    }
                                    fullWidth
                                />

                                <TextField
                                    variant="standard"
                                    label="Repetir Contraseña"
                                    type="password"
                                    autoComplete="new-password"
                                    name="clave2"
                                    value={values.clave2}
                                    onChange={e => {
                                        handleChange(e);
                                        validarClaves(values.clave1, e.target.value);
                                    }}
                                    onBlur={e => {
                                        handleBlur(e);
                                        validarClaves(values.clave1, values.clave2);
                                    }}
                                    error={Boolean(errors.clave2 && touched.clave2)}
                                    helperText={
                                        errors.clave2 && touched.clave2 ? errors.clave2 : ""
                                    }
                                    fullWidth
                                />

                                <Typography
                                    variant="body2"
                                    color={validador.longitud ? green[500] : red[500]}
                                    align="left"
                                >
                                    Al menos 8 caracteres
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color={validador.alfanumerico ? green[500] : red[500]}
                                    align="left"
                                >
                                    Letras y números
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color={validador.mayuscula && validador.minuscula ? green[500] : red[500]}
                                    align="left"
                                >
                                    Mayúsculas y minúsculas
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color={validador.caracterEspecial ? green[500] : red[500]}
                                    align="left"
                                >
                                    Caracteres especiales (por ejemplo: !@#$%)
                                </Typography>

                                {errorMessage && (
                                    <Typography
                                        variant="body2"
                                        color={red[500]}
                                        align="left"
                                        sx={{ mt: 1 }}
                                    >
                                        {errorMessage}
                                    </Typography>
                                )}

                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    disabled={isSubmitting || !(validador.coincidencia && validador.longitud && validador.alfanumerico && validador.mayuscula && validador.minuscula && validador.caracterEspecial)}
                                >
                                    Cambiar contraseña
                                </Button>
                            </Form>
                        )}
                    </Formik>
                </Fragment>

                {false && (
                    <Link
                        className={styles.a}
                        to="/dashboard"
                        style={{ color: theme.palette.secondary.main }}
                    >
                        ¿Olvidaste tu clave?
                    </Link>
                )}
            </Grid>
        </Layout>
    );
}

export default Page;
